import { Box, Grid, Typography, Skeleton, Tabs, Tab } from '@mui/material'
import { FONT, SIZE, WEIGHT } from '../../styles/foundation/typography'
import { NAVY, SUBTLE, BORDER, WHITE, PRIMARY_BLUE, withOpacity } from '../../styles/foundation/colors'

// Shared, modern KPI + outcome-tab UI for the NDR and NPR Call Recordings pages.
// Both pages compute their own numbers; this file owns only the look so the two stay
// in sync. Replaces the old flat pale-wash cards (bgcolor accent@8%) that read as "dead".

const CARD_SHADOW = '0 1px 3px rgba(16,42,67,0.05)'
const CARD_SHADOW_HOVER = '0 8px 22px rgba(16,42,67,0.10)'

const iconChipSx = (accent, size = 36) => ({
  width: size, height: size, borderRadius: '10px', flexShrink: 0,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  bgcolor: withOpacity(accent, 0.12), color: accent,
  '& .MuiSvgIcon-root': { fontSize: size * 0.5 },
})

// ── Hero KPI card (Total Calls / Connected / Avg Duration) ──────────────────
const heroCardSx = (accent) => ({
  position: 'relative', overflow: 'hidden', height: '100%',
  p: 2, borderRadius: '14px', bgcolor: WHITE,
  border: `1px solid ${BORDER}`, boxShadow: CARD_SHADOW,
  transition: 'transform .18s ease, box-shadow .18s ease',
  '&:hover': { transform: 'translateY(-2px)', boxShadow: CARD_SHADOW_HOVER },
  '&::before': {
    content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 3,
    background: `linear-gradient(90deg, ${accent}, ${withOpacity(accent, 0.35)})`,
  },
})

function HeroCard({ label, value, sub, color, icon }) {
  return (
    <Box sx={heroCardSx(color)}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25 }}>
        {icon && <Box sx={iconChipSx(color)}>{icon}</Box>}
        <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: WEIGHT.semibold, color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontFamily: FONT, fontWeight: WEIGHT.extra, fontSize: 28, color: NAVY, lineHeight: 1.1 }}>
        {value}
      </Typography>
      {sub != null && (
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', mt: 1, px: 1, py: '2px', borderRadius: '20px',
          bgcolor: withOpacity(color, 0.12), color,
        }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 11.5, fontWeight: WEIGHT.bold }}>{sub}</Typography>
        </Box>
      )}
    </Box>
  )
}

// ── Compact disposition card (Reattempt / RTO / Delivered / …) ──────────────
const dispCardSx = {
  height: '100%', p: 1.5, borderRadius: '12px', bgcolor: WHITE,
  border: `1px solid ${BORDER}`, boxShadow: CARD_SHADOW,
  transition: 'transform .18s ease, box-shadow .18s ease',
  '&:hover': { transform: 'translateY(-2px)', boxShadow: CARD_SHADOW_HOVER },
}

function DispositionCard({ label, value, color }) {
  return (
    <Box sx={dispCardSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.6 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
        <Typography sx={{ fontFamily: FONT, fontSize: 10, fontWeight: WEIGHT.medium, color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontFamily: FONT, fontWeight: WEIGHT.extra, fontSize: 18, color, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  )
}

export function CallKpiCards({ summaryCards, dispositionCards }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        {summaryCards.map((k) => (
          <Grid item xs={12} sm={4} key={k.label}>
            <HeroCard {...k} />
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={1.5}>
        {dispositionCards.map((k) => (
          <Grid item xs={6} sm={4} md={2} key={k.label}>
            <DispositionCard {...k} />
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}

export function CallKpiSkeleton() {
  return (
    <Box sx={{ mb: 2 }}>
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        {[0, 1, 2].map((i) => (
          <Grid item xs={12} sm={4} key={i}>
            <Box sx={heroCardSx(SUBTLE)}>
              <Skeleton variant="rounded" width={36} height={36} sx={{ mb: 1.25, borderRadius: '10px' }} />
              <Skeleton variant="text" width="45%" height={30} />
            </Box>
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={1.5}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Grid item xs={6} sm={4} md={2} key={i}>
            <Box sx={dispCardSx}>
              <Skeleton variant="text" width="70%" height={12} />
              <Skeleton variant="text" width="45%" height={22} />
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}

// ── Connected / Not Connected tabs ──────────────────────────────────────────
// Plain MUI underline tabs — the house pattern (see credit/index.js). Tabs hug their
// own label (no full-width colored blocks), single blue indicator under the active one,
// grey → navy on select. Icons inline, live count in the label. tabs: [{ key, label, icon, count }]
export function CallOutcomeTabs({ tabs, value, onChange }) {
  return (
    <Tabs
      value={value}
      onChange={(_e, v) => v && onChange(v)}
      variant="scrollable"
      scrollButtons={false}
      sx={{
        mb: 2, minHeight: 42,
        borderBottom: `1px solid ${BORDER}`,
        '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', backgroundColor: PRIMARY_BLUE },
        '& .MuiTab-root': {
          textTransform: 'none', minHeight: 42, minWidth: 'auto', px: 2, py: 1,
          fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.semibold, color: SUBTLE,
        },
        '& .Mui-selected': { color: `${NAVY} !important` },
      }}
    >
      {tabs.map((t) => (
        <Tab
          key={t.key}
          value={t.key}
          icon={t.icon}
          iconPosition="start"
          label={t.count != null ? `${t.label} (${t.count})` : t.label}
        />
      ))}
    </Tabs>
  )
}
